import React from 'react';
import { 
  FaStar, 
  FaRegStar, 
  FaEllipsisV, 
  FaChevronUp, 
  FaChevronDown,
  FaSort,
  FaTimes
} from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';

const MobileHeader = ({
  animateComponents,
  icon,
  title,
  canEditIdea,
  isPinned,
  handlePinIdea,
  showOptionsMenu,
  handleOptionsMenuToggle,
  menuButtonRef,
  mobileMenuOpen,
  setMobileMenuOpen,
  error,
  setError,
  isReordering,
  setIsReordering,
  components,
  description,
  handleEditIdea,
  tags,
  idea,
  mobileMenuRef
}) => {
  return (
    <>
      {/* Mobile Title Bar */}
      <div className={`lg:hidden sticky top-0 z-10 bg-gradient-to-r from-[var(--primary-light)] to-[var(--primary)] px-4 py-4 flex items-center justify-between ${animateComponents ? 'animate-slide-in-left' : 'opacity-0'}`}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className={`text-3xl flex-shrink-0 transition-transform duration-200 ${animateComponents ? 'animate-spin-in' : 'opacity-0'}`}>{icon}</span>
          <h1 className={`text-xl font-bold text-[var(--text-primary)] truncate ${animateComponents ? 'animate-fade-in-up' : 'opacity-0'}`}>{title}</h1>
        </div>
        <div className={`flex items-center gap-2 flex-shrink-0 ${animateComponents ? 'animate-fade-in-up' : 'opacity-0'}`}>
          {canEditIdea && (
            <button
              className="p-2 rounded-lg hover:bg-white/20 transition-all duration-200 hover:scale-110 active:scale-95"
              onClick={handlePinIdea}
              title={isPinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
            >
              {isPinned ? <FaStar className="text-[var(--text-primary)]" /> : <FaRegStar className="text-[var(--text-primary)] hover:animate-wiggle" />}
            </button>
          )}
          {canEditIdea && (
            <button
              ref={menuButtonRef}
              className="p-2 rounded-lg hover:bg-white/20 transition-all duration-200 hover:scale-110 active:scale-95"
              onClick={handleOptionsMenuToggle}
              title="More options"
            >
              <FaEllipsisV className={`text-[var(--text-primary)] transition-transform duration-300 ${showOptionsMenu ? 'rotate-90' : ''}`} />
            </button>
          )}
          <button
            className="p-2 rounded-lg hover:bg-white/20 transition-all duration-200 hover:scale-110 active:scale-95"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            title={mobileMenuOpen ? 'Hide details' : 'Show details'}
          >
            {mobileMenuOpen ? <FaChevronUp className="text-[var(--text-primary)]" /> : <FaChevronDown className="text-[var(--text-primary)]" />}
          </button>
        </div>
      </div>

      {/* Mobile Error Message */}
      {error && (
        <div className="lg:hidden mx-4 mb-4 p-3 border rounded-lg bg-danger-light relative" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>
          <div className="pr-8">{error}</div>
          <button
            onClick={() => setError('')}
            className="absolute top-2 right-2 p-1 hover:bg-[var(--bg-hover)] rounded transition-colors"
            title="Dismiss error"
          >
            <FaTimes className="text-danger hover:text-[var(--danger-dark)]" />
          </button>
          {error === 'You do not have permission to view this idea.' && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm flex items-center text-[var(--text-secondary)]">
                If you own this idea, your session may have expired. Try refreshing to re-authenticate.
                <button
                  className="ml-2 text-white text-xs font-semibold py-0.5 px-2 rounded shadow-sm bg-[var(--primary)] hover:opacity-90 transition-opacity"
                  onClick={() => window.location.reload()}
                >
                  Try to refresh
                </button>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Mobile Details Section with expanding animation */}
      <div
        ref={mobileMenuRef}
        className={`lg:hidden bg-[var(--bg-secondary)] px-2 transition-all duration-500 ease-in-out overflow-hidden ${mobileMenuOpen ? 'mb-4' : ''}`}
        style={{
          maxHeight: mobileMenuOpen ? `${(mobileMenuRef.current?.scrollHeight || 0) + 32}px` : '0px',
          paddingTop: mobileMenuOpen ? '16px' : '0px',
          paddingBottom: mobileMenuOpen ? '24px' : '0px',
          opacity: mobileMenuOpen ? 1 : 0,
          transform: mobileMenuOpen ? 'translateY(0)' : 'translateY(-10px)',
        }}
      >
        {/* Mobile Reordering Banner */}
        {isReordering && (
          <div className="mb-4 p-3 bg-[var(--primary)]/10 border border-[var(--primary)]/30 rounded-lg">
            <div className="flex items-center gap-2 text-[var(--primary)]">
              <FaSort className="animate-pulse" />
              <span className="text-sm font-medium">Reordering Mode Active</span>
              <button
                onClick={() => setIsReordering(false)}
                className="ml-auto text-xs px-2 py-1 bg-[var(--primary)] text-white rounded hover:opacity-80 transition-opacity"
              >
                Done
              </button>
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              {components.length > 0 ? 'Drag and drop components to reorder them' : 'Add some components first to enable reordering'}
            </p>
          </div>
        )}
        
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <p className={`text-[var(--text-primary)] whitespace-pre-line pr-16 ${animateComponents ? 'animate-fade-in-up' : 'opacity-0'}`}>
                {description || 'No description provided'}
              </p>
              {canEditIdea && (
                <button
                  className="absolute top-0 right-0 flex items-center gap-1 px-3 py-1 text-sm bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all duration-200 hover:scale-105"
                  onClick={handleEditIdea}
                >
                  <FiEdit2 size={14} /> Edit
                </button>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <div key={`tags-${idea?.id}`} className="flex overflow-x-auto gap-2 pb-2 scrollbar-thin scrollbar-thumb-[var(--primary)] scrollbar-track-[var(--bg-secondary)]">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className={`px-3 py-0.5 bg-[var(--bg-hover)] text-[var(--text-primary)] rounded text-sm whitespace-nowrap hover:bg-[var(--primary)] hover:text-white transition-all duration-200 ${animateComponents ? 'animate-tag-slide-in-subtle' : 'opacity-0'}`}
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="space-y-3 text-sm mb-2">
              <div>
                <span className="text-[var(--text-secondary)]">Status:</span>
                <span className="ml-2 text-[var(--text-primary)] capitalize">{idea?.status}</span>
              </div>
              <div>
                <span className="text-[var(--text-secondary)]">Created at:</span>
                <span className="ml-2 text-[var(--text-primary)]">
                  {idea && new Date(idea.created_at).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileHeader;
